/**
 * Configuration for the application
 * All configuration values are defined here - no hardcoded values elsewhere
 * 
 * TO UPDATE CONFIGURATION:
 * 1. API URL: Change apiUrl below
 * 2. Tenant ID: Change demo.tenantId to match your backend SEED_TENANT_ID
 * 3. Company ID: Change demo.companyId if needed
 * 
 * Last updated: 2026-01-27
 */

// API URL Configuration
// Automatically detects localhost (port 4001 for dev) vs production domain
const isLocal = typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');
const apiUrl = import.meta.env.VITE_API_URL || (isLocal ? 'http://localhost:4001' : (typeof window !== 'undefined' && window.location.origin ? window.location.origin : 'https://monsterado.com'));



export const config = {
  // API Configuration
  api: {
    baseUrl: apiUrl,
    baseUrlWithoutApi: apiUrl,
    timeout: 30000,
  },

  // Authentication
  auth: {
    jwtSecret: 'dev-secret',
  },

  // Demo Configuration
  // Update these values to match your backend SEED_TENANT_ID
  demo: {
    tenantId: 'tenant_1234566', // Must match SEED_TENANT_ID in backend .env
    companyId: 'seed-company-1',
  },

  // App Configuration
  app: {
    name: 'Monsterado Lounge',
    version: '1.0.1', // Updated version
  },

  // Visitor Tracking Configuration
  tracking: {
    enabled: true,
    cookieExpiry: 365 * 24 * 60 * 60 * 1000, // 1 year in milliseconds
    sessionTimeout: 30 * 60 * 1000, // 30 minutes in milliseconds
    trackPageViews: true,
    trackEvents: true,
    trackUserInteractions: true,
    sendToBackend: false, // Set to true when backend endpoint is ready
    backendEndpoint: '/api/analytics/track',
  },
} as const;

// Helper functions for common configurations
export const getApiUrl = (endpoint: string = '') => {
  const baseUrl = config.api.baseUrl.replace(/\/$/, ''); // Remove trailing slash
  const cleanEndpoint = endpoint.replace(/^\//, ''); // Remove leading slash
  const fullUrl = cleanEndpoint ? `${baseUrl}/${cleanEndpoint}` : baseUrl;
  return fullUrl;
};

export const getAuthHeaders = () => ({
  'Content-Type': 'application/json',
  'x-tenant-id': getTenantId(),
});

export const getCompanyId = (): string => {
  if (typeof window !== 'undefined') {
    const company_id = localStorage.getItem('company_id');
    const companyId = localStorage.getItem('companyId');


    const result = company_id || companyId ;
    if (result) {
      return result;
    }

    // For guest users who haven't created a company yet, return a fallback
    // This prevents errors during registration/login flow
    return 'guest-company';
  }

  throw new Error('Company ID not available in server environment');
};

export const getTenantId = (): string => {
  if (typeof window !== 'undefined') {
    const tenantId = localStorage.getItem('tenant_id');
    if (tenantId) {
      return tenantId;
    }
    
    const token = localStorage.getItem('auth_token');
    if (token) {
      try {
        const base64Url = token.split('.')[1];
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        const jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
          return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
        }).join(''));
        const payload = JSON.parse(jsonPayload);
        if (payload.tenantId) {
          localStorage.setItem('tenant_id', payload.tenantId);
          return payload.tenantId;
        }
      } catch (error) {
        // Silent fail
      }
    }
    
    return config.demo.tenantId;
  }
  
  throw new Error('Tenant ID not available in server environment');
};

// Get headers for API requests
export const getHeaders = () => {
  const headers = {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${localStorage.getItem('auth_token') || ''}`,
    'x-tenant-id': getTenantId(),
    'x-company-id': getCompanyId(),
  };
  
  return headers;
};